import React, { useState } from 'react';
import { useAdmin } from '../../context/AdminContext';
import { PaymentLogo } from '../../components/common/BrandLogo';
import {
  Wallet as WalletIcon,
  RefreshCw,
  FileText,
  CheckCircle2,
  XCircle,
  Check,
  Copy,
} from 'lucide-react';

export const AdminDepositsPage: React.FC = () => {
  const {
    deposits,
    loading,
    actionLoading,
    refresh,
    approveDeposit,
    rejectDeposit,
  } = useAdmin();

  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('all');
  const [selectedSlipUrl, setSelectedSlipUrl] = useState<string | null>(null);
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [copiedRef, setCopiedRef] = useState<string | null>(null);

  const handleCopyRef = (ref: string, id: string) => {
    navigator.clipboard.writeText(ref);
    setCopiedRef(id);
    setTimeout(() => setCopiedRef(null), 1500);
  };

  const handleApprove = async (depId: string) => {
    if (!confirm('ဘဏ်/KPay ထဲ ငွေအမှန်တကယ် ဝင်ကြောင်း စစ်ဆေးပြီးပြီလား?')) return;
    try {
      await approveDeposit(depId);
    } catch (err: any) {
      alert(err.message || 'Deposit approval failed');
    }
  };

  const handleReject = async () => {
    if (!rejectingId) return;
    if (!rejectReason.trim()) {
      alert('ငြင်းပယ်ရသည့် အကြောင်းရင်းကို ဖြည့်ပေးပါ');
      return;
    }
    try {
      await rejectDeposit(rejectingId, rejectReason);
      setRejectingId(null);
      setRejectReason('');
    } catch (err: any) {
      alert(err.message || 'Deposit rejection failed');
    }
  };

  const filteredDeposits = deposits.filter((dep) => {
    if (statusFilter === 'all') return true;
    if (statusFilter === 'pending') {
      return dep.status === 'pending' || dep.status === 'under_review';
    }
    if (statusFilter === 'approved') {
      return dep.status === 'credited';
    }
    if (statusFilter === 'rejected') {
      return dep.status === 'rejected';
    }
    return true;
  });

  return (
    <div className="space-y-6 pb-28">
      {/* Title & Refresh */}
      <div className="px-1">
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-base sm:text-lg font-bold text-plum-dark flex items-center space-x-2 min-w-0">
            <WalletIcon className="w-5 h-5 text-primary shrink-0" />
            <span className="truncate">ငွေဖြည့် စာရင်း</span>
          </h2>

          <button
            onClick={refresh}
            title="အသစ်ပြန်တင်မည်"
            disabled={loading}
            className="p-1.5 sm:p-2 rounded-xl bg-surface-card border border-plum-border text-plum-dark hover:text-primary transition shrink-0"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin text-primary' : ''}`} />
          </button>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex space-x-1.5 overflow-x-auto pb-1 scrollbar-none">
        {[
          { id: 'all', label: 'အားလုံး', count: deposits.length },
          {
            id: 'pending',
            label: 'စစ်ဆေးဆဲ',
            count: deposits.filter((d) => d.status === 'pending' || d.status === 'under_review').length,
          },
          {
            id: 'approved',
            label: 'အတည်ပြုပြီး',
            count: deposits.filter((d) => d.status === 'credited').length,
          },
          {
            id: 'rejected',
            label: 'ငြင်းပယ်ပြီး',
            count: deposits.filter((d) => d.status === 'rejected').length,
          },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setStatusFilter(tab.id as any)}
            className={`px-3.5 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition ${
              statusFilter === tab.id
                ? 'bg-primary text-white shadow-xs'
                : 'bg-surface-card border border-plum-border text-plum-muted hover:text-plum-dark'
            }`}
          >
            {tab.label} <span className="opacity-70 text-[10px]">({tab.count})</span>
          </button>
        ))}
      </div>

      {/* Deposits List */}
      <div className="space-y-3">
        {filteredDeposits.map((dep) => {
          const isPending = dep.status === 'pending' || dep.status === 'under_review';
          const isApproved = dep.status === 'credited';
          const isRejected = dep.status === 'rejected';

          return (
            <div
              key={dep.id}
              className={`bg-surface-card rounded-2xl border p-4 shadow-xs space-y-3 transition ${
                isPending ? 'border-primary/40 shadow-sm' : 'border-plum-border'
              }`}
            >
              {/* Header */}
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-3">
                  <PaymentLogo method={dep.payment_method} size="md" className="rounded-xl shadow-xs" />
                  <div>
                    <span className="font-bold text-plum-dark text-base">
                      +{dep.amount.toLocaleString()} Ks
                    </span>
                    <p className="text-xs text-plum-muted uppercase font-mono mt-0.5">
                      {dep.payment_method}
                    </p>
                    <p className="text-[11px] text-plum-muted mt-0.5">
                      User: <span className="font-mono">{dep.user_id}</span>
                    </p>
                  </div>
                </div>

                <span
                  className={`text-[10px] font-bold px-2.5 py-1 rounded-full border ${
                    isApproved
                      ? 'bg-green-500/10 text-status-green border-green-500/30'
                      : isRejected
                      ? 'bg-red-500/10 text-status-red border-red-500/30'
                      : 'bg-amber-500/10 text-status-amber border-amber-500/30'
                  }`}
                >
                  {isApproved
                    ? '✅ အတည်ပြုပြီး'
                    : isRejected
                    ? '❌ ငြင်းပယ်ပြီး'
                    : '⏳ စစ်ဆေးဆဲ'}
                </span>
              </div>

              {/* Reference & Slip Row */}
              <div className="bg-surface-page p-2.5 rounded-xl border border-plum-border/60 flex items-center justify-between text-xs">
                <div className="flex items-center space-x-2">
                  <span className="text-[11px] text-plum-muted">Ref:</span>
                  <span className="font-mono font-semibold text-plum-dark">
                    {dep.tx_ref || 'မရှိပါ'}
                  </span>
                  {dep.tx_ref && (
                    <button
                      onClick={() => handleCopyRef(dep.tx_ref!, dep.id)}
                      className="p-1 text-plum-muted hover:text-primary transition"
                      title="နံပါတ် ကူးယူရန်"
                    >
                      {copiedRef === dep.id ? (
                        <Check className="w-3.5 h-3.5 text-status-green" />
                      ) : (
                        <Copy className="w-3.5 h-3.5" />
                      )}
                    </button>
                  )}
                </div>

                <div className="flex items-center space-x-2">
                  {dep.slip_url ? (
                    <button
                      onClick={() => setSelectedSlipUrl(dep.slip_url!)}
                      className="flex items-center space-x-1 text-[11px] font-bold text-primary bg-primary/10 hover:bg-primary/20 px-2.5 py-1 rounded-lg transition"
                    >
                      <FileText className="w-3.5 h-3.5" />
                      <span>Slip ကြည့်မည်</span>
                    </button>
                  ) : (
                    <span className="text-[10px] text-plum-muted italic">No slip image</span>
                  )}
                </div>
              </div>

              {/* Timestamp */}
              <div className="flex items-center justify-between text-[11px] text-plum-muted">
                <span>စတင်ချိန်: {new Date(dep.created_at).toLocaleString()}</span>
              </div>

              {/* Action Buttons for Pending */}
              {isPending && (
                <div className="pt-2 border-t border-plum-border/60 grid grid-cols-2 gap-2">
                  <button
                    onClick={() => {
                      setRejectingId(dep.id);
                      setRejectReason('');
                    }}
                    disabled={actionLoading}
                    className="border border-red-500/40 hover:bg-red-500/10 text-status-red font-bold text-xs py-2.5 rounded-xl transition touch-target flex items-center justify-center space-x-1"
                  >
                    <XCircle className="w-4 h-4 text-status-red" />
                    <span>ငြင်းပယ်မည်</span>
                  </button>

                  <button
                    onClick={() => handleApprove(dep.id)}
                    disabled={actionLoading}
                    className="bg-status-green hover:bg-green-600 active:scale-[0.98] text-white font-bold text-xs py-2.5 rounded-xl transition shadow-xs touch-target flex items-center justify-center space-x-1"
                  >
                    <CheckCircle2 className="w-4 h-4 text-white" />
                    <span>အတည်ပြုမည်</span>
                  </button>
                </div>
              )}
            </div>
          );
        })}

        {filteredDeposits.length === 0 && (
          <div className="p-8 text-center bg-surface-card rounded-2xl border border-plum-border text-plum-muted text-xs">
            {statusFilter === 'all'
              ? 'ငွေသွင်းမှတ်တမ်း မရှိသေးပါ။'
              : 'ဤအခြေအနေရှိ ငွေသွင်းလွှာ မရှိပါ။'}
          </div>
        )}
      </div>

      {/* Slip Image Zoom Modal */}
      {selectedSlipUrl && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
          onClick={() => setSelectedSlipUrl(null)}
        >
          <div
            className="bg-surface-card max-w-sm w-full rounded-3xl border border-plum-border p-4 shadow-2xl space-y-3"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-bold text-plum-dark">ငွေလွှဲပြေစာ Screenshot</h3>
              <button
                onClick={() => setSelectedSlipUrl(null)}
                className="p-1 text-plum-muted hover:text-plum-dark"
              >
                ✕
              </button>
            </div>
            <div className="rounded-2xl overflow-hidden border border-plum-border bg-black max-h-[70vh] flex items-center justify-center">
              <img
                src={selectedSlipUrl}
                alt="Payment Slip Proof"
                className="max-h-[70vh] w-auto object-contain"
              />
            </div>
            <button
              onClick={() => setSelectedSlipUrl(null)}
              className="w-full py-2 bg-surface-page border border-plum-border text-xs font-semibold rounded-xl text-plum-dark"
            >
              ပိတ်မည်
            </button>
          </div>
        </div>
      )}

      {/* Reject Reason Dialog */}
      {rejectingId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-xs p-4">
          <div className="bg-surface-card w-full max-w-sm rounded-3xl border border-plum-border p-5 shadow-2xl space-y-4">
            <h3 className="font-bold text-plum-dark text-base">ငွေသွင်းမှု ငြင်းပယ်ရန်</h3>
            <p className="text-xs text-plum-muted">အကြောင်းရင်းကို ဖြည့်ပေးပါ (ဥပမာ: ငွေမဝင်ပါ၊ ပြေစာအတုဖြစ်ပါသည်)</p>

            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="ငြင်းပယ်ရသည့် အကြောင်းရင်း..."
              rows={3}
              className="w-full bg-surface-page border border-plum-border rounded-xl p-3 text-xs text-plum-dark"
            />

            <div className="flex space-x-2 pt-2">
              <button
                type="button"
                onClick={() => setRejectingId(null)}
                className="flex-1 border border-plum-border py-2.5 rounded-xl text-xs font-semibold text-plum-muted hover:text-plum-dark"
              >
                ပယ်ဖျက်မည်
              </button>
              <button
                type="button"
                disabled={actionLoading}
                onClick={handleReject}
                className="flex-1 bg-status-red text-white py-2.5 rounded-xl text-xs font-semibold hover:bg-red-600 transition"
              >
                {actionLoading ? 'ဆောင်ရွက်နေပါသည်...' : 'ငြင်းပယ်မည်'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
